import {Component, OnInit} from '@angular/core';
import {ApiService} from '../../core/http/api.service';
import {DashboardService} from '../dashboard.service';
import {UtilsService} from '../../core/services/utils/utils.service';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import {createCroppedImageFile} from '../../core/services/utils/image-upload.utils';

interface MarketplaceBookForm {
  id: string;
  coverImageUrl: string;
  name: string;
  category: string;
  tagline: string;
  authorUserId: string;
  authorName: string;
  description: string;
  status: string;
  amazonUrl: string;
  flipkartUrl: string;
  otherLinks: Array<{ name: string; url: string }>;
  podcastAssociationMode: string;
  authorPodcastIds: string[];
  manualPodcastIds: string[];
}

@Component({
    selector: 'app-marketplace-books',
    templateUrl: './marketplace-books.component.html',
    styleUrls: ['./marketplace-books.component.css'],
    standalone: false
})
export class MarketplaceBooksComponent implements OnInit {

  loading = true;
  saving = false;
  uploadingCover = false;
  showBookModal = false;
  isEditMode = false;
  page = 1;
  perPage = 10;
  loaders = Array(10).fill(0);
  books: any[] = [];
  userKeyword = '';
  lookupUsers: any[] = [];
  authorSuggestionsVisible = false;
  authorSearchLoading = false;
  authorBlurTimeout: any = null;
  authorSearchDebounce: any = null;
  authorPodcastsLoading = false;
  authorPodcastOptions: any[] = [];
  manualPodcastsLoading = false;
  manualPodcastOptions: any[] = [];
  showCoverCropModal = false;
  coverImageFile: File = null;
  croppedCoverFile: File = null;
  readonly coverAspectRatio = 3 / 4;
  filters = {
    keyword: '',
    status: 'all',
    category: 'all'
  };
  categoryOptions = [
    {label: 'Religious', value: 'religious'},
    {label: 'Political', value: 'political'},
    {label: 'Social', value: 'social'},
    {label: 'Historical', value: 'historical'},
    {label: 'Other', value: 'other'},
  ];
  statusOptions = [
    {label: 'Draft', value: 'draft'},
    {label: 'Pending', value: 'pending'},
    {label: 'Published', value: 'published'},
    {label: 'Rejected', value: 'rejected'},
    {label: 'Archived', value: 'archived'},
  ];
  bookForm: MarketplaceBookForm = this.createEmptyBookForm();

  constructor(
    private api: ApiService,
    private dashApi: DashboardService,
    private utils: UtilsService
  ) {
  }

  ngOnInit(): void {
    this.getUsers('');
    this.getBooks();
  }

  createEmptyBookForm(): MarketplaceBookForm {
    return {
      id: '',
      coverImageUrl: '',
      name: '',
      category: 'other',
      tagline: '',
      authorUserId: '',
      authorName: '',
      description: '',
      status: 'published',
      amazonUrl: '',
      flipkartUrl: '',
      otherLinks: [{name: '', url: ''}],
      podcastAssociationMode: 'author',
      authorPodcastIds: [],
      manualPodcastIds: []
    };
  }

  get filteredBooks(): any[] {
    const keyword = (this.filters.keyword || '').trim().toLowerCase();

    return (this.books || []).filter(book => {
      const matchesKeyword = !keyword || [
        book?.name,
        book?.author?.name,
        book?.author?.email,
        book?.description,
        book?.tagline
      ].some(value => String(value || '').toLowerCase().includes(keyword));

      const matchesStatus = this.filters.status === 'all' || book?.status === this.filters.status;
      const matchesCategory = this.filters.category === 'all' || book?.category === this.filters.category;

      return matchesKeyword && matchesStatus && matchesCategory;
    });
  }

  getBooks(): void {
    this.loading = true;
    this.api.getMarketplaceBooks().subscribe(res => {
      this.books = Array.isArray(res?.data) ? res.data : [];
      this.loading = false;
    }, err => {
      this.loading = false;
      this.utils.resErr(err);
    });
  }

  getUsers(keyword: string = ''): void {
    this.userKeyword = String(keyword || '').trim();
    this.authorSearchLoading = true;
    this.api.getAllUsers({page: 1, per_page: 30, keyword: this.userKeyword}).subscribe(res => {
      this.lookupUsers = res.response?.users || [];
      this.authorSearchLoading = false;
    }, () => {
      this.lookupUsers = [];
      this.authorSearchLoading = false;
    });
  }

  getUserDisplayName(user: any = {}): string {
    const firstName = String(user?.firstName || '').trim();
    const lastName = String(user?.lastName || '').trim();
    return `${firstName} ${lastName}`.trim();
  }

  clearAuthorUserSelection(preserveText = true): void {
    this.bookForm.authorUserId = '';
    if (!preserveText) {
      this.bookForm.authorName = '';
    }
    this.bookForm.authorPodcastIds = [];
    this.authorPodcastOptions = [];
    this.authorPodcastsLoading = false;
    if (this.bookForm.podcastAssociationMode === 'author') {
      this.bookForm.podcastAssociationMode = 'none';
    }
  }

  setPodcastAssociationToNone(): void {
    this.bookForm.podcastAssociationMode = 'none';
    this.bookForm.authorPodcastIds = [];
    this.bookForm.manualPodcastIds = [];
  }

  onAuthorInput(value: string = ''): void {
    if (this.authorBlurTimeout) {
      clearTimeout(this.authorBlurTimeout);
      this.authorBlurTimeout = null;
    }
    if (this.authorSearchDebounce) {
      clearTimeout(this.authorSearchDebounce);
    }

    this.bookForm.authorName = value || '';
    if (this.bookForm.authorUserId) {
      this.clearAuthorUserSelection(true);
    }

    const keyword = String(value || '').trim();
    if (!keyword) {
      this.lookupUsers = [];
      this.authorSuggestionsVisible = false;
      return;
    }

    // When admin types a custom author (no mapped Bhakts user), force podcast association to "No podcasts".
    if (!this.bookForm.authorUserId) {
      this.setPodcastAssociationToNone();
    }

    this.authorSuggestionsVisible = true;
    this.authorSearchDebounce = setTimeout(() => {
      this.getUsers(keyword);
    }, 250);
  }

  onAuthorInputFocus(): void {
    if (this.authorBlurTimeout) {
      clearTimeout(this.authorBlurTimeout);
      this.authorBlurTimeout = null;
    }
    const keyword = String(this.bookForm.authorName || '').trim();
    if (!keyword) {
      this.authorSuggestionsVisible = false;
      return;
    }

    this.authorSuggestionsVisible = true;
    this.getUsers(keyword);
  }

  onAuthorInputBlur(): void {
    this.authorBlurTimeout = setTimeout(() => {
      this.authorSuggestionsVisible = false;
    }, 150);
  }

  selectAuthorSuggestion(user: any = {}): void {
    const selectedUserId = String(user?.id || '').trim();
    if (!selectedUserId) {
      return;
    }

    if (this.authorBlurTimeout) {
      clearTimeout(this.authorBlurTimeout);
      this.authorBlurTimeout = null;
    }

    this.bookForm.authorUserId = selectedUserId;
    this.bookForm.authorName = this.getUserDisplayName(user);
    this.authorSuggestionsVisible = false;
    if (this.bookForm.podcastAssociationMode === 'author') {
      this.loadAuthorPodcasts(selectedUserId);
    }
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.bookForm = this.createEmptyBookForm();
    this.authorSuggestionsVisible = false;
    this.lookupUsers = [];
    this.authorPodcastOptions = [];
    this.manualPodcastOptions = [];
    this.manualPodcastsLoading = false;
    this.showCoverCropModal = false;
    this.coverImageFile = null;
    this.croppedCoverFile = null;
    this.showBookModal = true;
  }

  openEditModal(book): void {
    this.isEditMode = true;
    this.bookForm = this.mapBookToForm(book);
    this.authorSuggestionsVisible = false;
    this.lookupUsers = [];
    this.ensureAuthorInLookup(book?.author);
    if (this.bookForm.authorUserId) {
      this.loadAuthorPodcasts(this.bookForm.authorUserId);
    } else {
      this.authorPodcastOptions = [];
      this.bookForm.authorPodcastIds = [];
      if (this.bookForm.podcastAssociationMode === 'author') {
        this.bookForm.podcastAssociationMode = 'none';
      }
    }
    if (this.bookForm.podcastAssociationMode === 'manual') {
      this.loadManualPodcasts();
    }
    this.showBookModal = true;
    this.showCoverCropModal = false;
    this.coverImageFile = null;
    this.croppedCoverFile = null;
  }

  closeBookModal(): void {
    if (this.saving || this.uploadingCover) {
      return;
    }
    this.showBookModal = false;
    this.showCoverCropModal = false;
    this.coverImageFile = null;
    this.croppedCoverFile = null;
    this.bookForm = this.createEmptyBookForm();
    this.authorPodcastOptions = [];
    this.authorPodcastsLoading = false;
    this.manualPodcastOptions = [];
    this.manualPodcastsLoading = false;
    this.isEditMode = false;
    this.authorSuggestionsVisible = false;
    this.lookupUsers = [];
  }

  mapBookToForm(book): MarketplaceBookForm {
    const buyLinks = Array.isArray(book?.buyLinks) ? book.buyLinks : [];
    const amazonLink = this.findBuyLink(buyLinks, 'amazon');
    const flipkartLink = this.findBuyLink(buyLinks, 'flipkart');
    const otherLinks = buyLinks
      .filter(link => !this.isPlatformLink(link, 'amazon') && !this.isPlatformLink(link, 'flipkart'))
      .map(link => ({
        name: link?.name || '',
        url: link?.url || ''
      }));

    const podcastAssociation = book?.podcastAssociation || {};

    const authorUserId = String(book?.author?.id || '').trim();
    const authorName = String(book?.author?.name || book?.authorName || '').trim();

    return {
      id: book?.id || '',
      coverImageUrl: book?.coverImageUrl || '',
      name: book?.name || '',
      category: book?.category || 'other',
      tagline: book?.tagline || '',
      authorUserId,
      authorName,
      description: book?.description || '',
      status: book?.status || 'published',
      amazonUrl: amazonLink?.url || '',
      flipkartUrl: flipkartLink?.url || '',
      otherLinks: otherLinks.length ? otherLinks : [{name: '', url: ''}],
      podcastAssociationMode: podcastAssociation.mode || 'author',
      authorPodcastIds: Array.isArray(podcastAssociation.podcastIds)
        ? podcastAssociation.podcastIds
        : [],
      manualPodcastIds: Array.isArray(podcastAssociation.podcastIds)
        ? podcastAssociation.podcastIds
        : []
    };
  }

  findBuyLink(links: any[] = [], platform: string): any {
    return links.find(link => this.isPlatformLink(link, platform));
  }

  isPlatformLink(link: any = {}, platform: string = ''): boolean {
    const normalizedPlatform = String(link?.platform || '').toLowerCase();
    const normalizedName = String(link?.name || '').toLowerCase();
    return normalizedPlatform === platform || normalizedName.includes(platform);
  }

  ensureAuthorInLookup(author): void {
    if (!author?.id) {
      return;
    }

    const exists = this.lookupUsers.some(user => user.id === author.id);
    if (!exists) {
      const [firstName, ...lastNameParts] = String(author.name || '').split(' ');
      this.lookupUsers = [
        {
          id: author.id,
          firstName: firstName || author.name || '',
          lastName: lastNameParts.join(' '),
          email: author.email || ''
        },
        ...this.lookupUsers
      ];
    }
  }

  onPodcastAssociationModeChange(): void {
    if (this.bookForm.podcastAssociationMode === 'author') {
      this.bookForm.manualPodcastIds = [];
      this.loadAuthorPodcasts(this.bookForm.authorUserId);
      return;
    }

    if (this.bookForm.podcastAssociationMode === 'manual') {
      this.bookForm.authorPodcastIds = [];
      this.loadManualPodcasts();
      return;
    }

    this.bookForm.authorPodcastIds = [];
    this.bookForm.manualPodcastIds = [];
  }

  loadAuthorPodcasts(userId: string = ''): void {
    const authorId = String(userId || '').trim();
    if (!authorId) {
      this.authorPodcastOptions = [];
      this.authorPodcastsLoading = false;
      this.bookForm.authorPodcastIds = [];
      return;
    }

    this.authorPodcastsLoading = true;
    this.api.getPodcasts({
      page: 1,
      per_page: 200,
      sort_by: 'updated_desc',
      userId: authorId
    }).subscribe(res => {
      const response = res?.response || {};
      const podcasts = Array.isArray(response?.podcasts) ? response.podcasts : [];
      this.authorPodcastOptions = podcasts
        .filter(item => !!item?.id)
        .map(item => this.buildAuthorPodcastOption(item));

      const allowedIds = new Set(this.authorPodcastOptions.map(item => item.id));
      const matchedSelections = (this.bookForm.authorPodcastIds || []).filter(id => allowedIds.has(id));
      this.bookForm.authorPodcastIds = matchedSelections.length
        ? matchedSelections
        : this.authorPodcastOptions.map(item => item.id);
      this.authorPodcastsLoading = false;
    }, err => {
      this.authorPodcastsLoading = false;
      this.authorPodcastOptions = [];
      this.bookForm.authorPodcastIds = [];
      this.utils.resErr(err);
    });
  }

  isAuthorPodcastSelected(podcastId: string = ''): boolean {
    return (this.bookForm.authorPodcastIds || []).includes(String(podcastId || ''));
  }

  toggleAuthorPodcastSelection(podcastId: string = '', checked: boolean = false): void {
    const id = String(podcastId || '').trim();
    if (!id) {
      return;
    }

    const selectedSet = new Set((this.bookForm.authorPodcastIds || []).map(item => String(item)));
    if (checked) {
      selectedSet.add(id);
    } else {
      selectedSet.delete(id);
    }

    this.bookForm.authorPodcastIds = Array.from(selectedSet);
  }

  loadManualPodcasts(): void {
    this.manualPodcastsLoading = true;
    this.api.getPodcasts({
      page: 1,
      per_page: 200,
      sort_by: 'updated_desc'
    }).subscribe(res => {
      const response = res?.response || {};
      const podcasts = Array.isArray(response?.podcasts) ? response.podcasts : [];
      this.manualPodcastOptions = podcasts
        .filter(item => !!(item?.id || item?._id))
        .map(item => this.buildAuthorPodcastOption(item));

      const allowedIds = new Set(this.manualPodcastOptions.map(item => item.id));
      const matchedSelections = (this.bookForm.manualPodcastIds || []).filter(id => allowedIds.has(id));
      this.bookForm.manualPodcastIds = matchedSelections;
      this.manualPodcastsLoading = false;
    }, err => {
      this.manualPodcastsLoading = false;
      this.manualPodcastOptions = [];
      this.bookForm.manualPodcastIds = [];
      this.utils.resErr(err);
    });
  }

  isManualPodcastSelected(podcastId: string = ''): boolean {
    return (this.bookForm.manualPodcastIds || []).includes(String(podcastId || ''));
  }

  toggleManualPodcastSelection(podcastId: string = '', checked: boolean = false): void {
    const id = String(podcastId || '').trim();
    if (!id) {
      return;
    }

    const selectedSet = new Set((this.bookForm.manualPodcastIds || []).map(item => String(item)));
    if (checked) {
      selectedSet.add(id);
    } else {
      selectedSet.delete(id);
    }

    this.bookForm.manualPodcastIds = Array.from(selectedSet);
  }

  openPodcastPreview(url: string = '', event: Event = null): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const mediaUrl = String(url || '').trim();
    if (!mediaUrl) {
      return;
    }

    window.open(mediaUrl, '_blank');
  }

  buildAuthorPodcastOption(podcast: any = {}): any {
    const id = String(podcast?.id || podcast?._id || '').trim();
    const shortId = id ? id.slice(-6).toUpperCase() : 'NA';
    const title = String(podcast?.title || '').trim() || `Podcast ${shortId}`;
    const createdAt = this.formatPodcastDate(podcast?.createdAt);
    const mediaType = this.getPodcastMediaType(podcast?.media);
    const mediaUrl = String(podcast?.media?.location || '').trim();
    const thumbnail = this.getPodcastThumbnail(podcast);

    return {
      id,
      title,
      shortId,
      createdAt,
      mediaType,
      mediaUrl,
      thumbnail,
      label: `${title} (${shortId})`
    };
  }

  getPodcastThumbnail(podcast: any = {}): string {
    const mediaThumb = String(podcast?.media?.thumb || '').trim();
    if (mediaThumb) {
      return mediaThumb;
    }

    const location = String(podcast?.media?.location || '').trim();
    const youtubeVideoId = this.extractYoutubeVideoId(location);
    if (youtubeVideoId) {
      return `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`;
    }

    return '';
  }

  getPodcastMediaType(media: any = null): string {
    const mimeType = String(media?.mimetype || '').toLowerCase();
    const location = String(media?.location || '').toLowerCase();

    if (mimeType.includes('youtube') || location.includes('youtube.com') || location.includes('youtu.be')) {
      return 'YouTube';
    }

    if (mimeType.startsWith('video/')) {
      return 'Video';
    }

    return 'Media';
  }

  formatPodcastDate(value: any = null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  extractYoutubeVideoId(url: string = ''): string {
    const value = String(url || '').trim();
    const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
    const matches = value.match(youtubeRegex);
    return matches ? matches[1] : '';
  }

  uploadCover(event): void {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    this.coverImageFile = file;
    this.croppedCoverFile = null;
    this.showCoverCropModal = true;
    event.target.value = '';
  }

  closeCoverCropModal(): void {
    if (this.uploadingCover) {
      return;
    }
    this.showCoverCropModal = false;
    this.coverImageFile = null;
    this.croppedCoverFile = null;
  }

  onCoverImageCropped(event: ImageCroppedEvent): void {
    this.croppedCoverFile = createCroppedImageFile(event, `book-cover-${Date.now()}.png`);
  }

  onCoverImageLoadFailed(): void {
    this.utils.toastError('Image load failed. Please choose another image.');
    this.closeCoverCropModal();
  }

  uploadCroppedCover(): void {
    if (!this.croppedCoverFile) {
      this.utils.toastError('Please adjust crop area before uploading');
      return;
    }

    this.uploadingCover = true;
    const reader = new FileReader();
    reader.readAsDataURL(this.croppedCoverFile);
    reader.onload = () => {
      this.dashApi.uploadImage({file: reader.result}).subscribe(res => {
        this.bookForm.coverImageUrl = res.response?.url || '';
        this.uploadingCover = false;
        this.closeCoverCropModal();
      }, err => {
        this.uploadingCover = false;
        this.utils.resErr(err);
      });
    };
  }

  handleCoverUploadClick(): void {
    const ele = document.getElementById('book_cover_upload');
    if (!!ele) {
      ele.click();
    }
  }

  addOtherLink(): void {
    this.bookForm.otherLinks.push({name: '', url: ''});
  }

  removeOtherLink(index: number): void {
    if (this.bookForm.otherLinks.length === 1) {
      this.bookForm.otherLinks[0] = {name: '', url: ''};
      return;
    }
    this.bookForm.otherLinks.splice(index, 1);
  }

  buildPayload(form: MarketplaceBookForm = this.bookForm, statusOverride: string = ''): any {
    const buyLinks = [];
    const amazonUrl = (form.amazonUrl || '').trim();
    const flipkartUrl = (form.flipkartUrl || '').trim();

    if (amazonUrl) {
      buyLinks.push({platform: 'amazon', name: 'Amazon', url: amazonUrl});
    }

    if (flipkartUrl) {
      buyLinks.push({platform: 'flipkart', name: 'Flipkart', url: flipkartUrl});
    }

    (form.otherLinks || []).forEach(link => {
      const name = (link.name || '').trim();
      const url = (link.url || '').trim();
      if (name && url) {
        buyLinks.push({platform: 'other', name, url});
      }
    });

    const manualPodcastIds = Array.isArray(form.manualPodcastIds)
      ? form.manualPodcastIds.map(item => String(item || '').trim()).filter(Boolean)
      : [];

    const authorPodcastIds = Array.isArray(form.authorPodcastIds)
      ? form.authorPodcastIds.map(item => String(item || '').trim()).filter(Boolean)
      : [];

    const podcastIds = form.podcastAssociationMode === 'author'
      ? authorPodcastIds
      : form.podcastAssociationMode === 'manual'
        ? manualPodcastIds
        : [];

    return {
      coverImageUrl: (form.coverImageUrl || '').trim(),
      name: (form.name || '').trim(),
      category: form.category || 'other',
      tagline: (form.tagline || '').trim(),
      authorUserId: (form.authorUserId || '').trim() || null,
      authorName: (form.authorName || '').trim() || null,
      description: (form.description || '').trim(),
      buyLinks,
      status: statusOverride || form.status || 'published',
      podcastAssociation: {
        mode: form.podcastAssociationMode || 'author',
        podcastIds
      }
    };
  }

  validateForm(): boolean {
    if (!this.bookForm.coverImageUrl) {
      this.utils.toastError('Please upload a cover image');
      return false;
    }
    if (!(this.bookForm.name || '').trim()) {
      this.utils.toastError('Please enter book name');
      return false;
    }
    const authorUserId = (this.bookForm.authorUserId || '').trim();
    const authorName = (this.bookForm.authorName || '').trim();
    if (!authorUserId && !authorName) {
      this.utils.toastError('Please select or enter book author');
      return false;
    }
    if (!(this.bookForm.description || '').trim()) {
      this.utils.toastError('Please enter book description');
      return false;
    }
    if (this.bookForm.podcastAssociationMode === 'author' && !authorUserId) {
      this.utils.toastError('For author podcasts, please select a Bhakts user author or choose another association mode');
      return false;
    }
    if (
      this.bookForm.podcastAssociationMode === 'author' &&
      this.authorPodcastOptions.length > 0 &&
      !(this.bookForm.authorPodcastIds || []).length
    ) {
      this.utils.toastError('Please select at least one author podcast or choose "No podcasts"');
      return false;
    }
    if (
      this.bookForm.podcastAssociationMode === 'manual' &&
      !(this.bookForm.manualPodcastIds || []).length
    ) {
      this.utils.toastError('Please choose at least one podcast or choose "No podcasts"');
      return false;
    }
    return true;
  }

  saveBook(): void {
    if (!this.validateForm() || this.saving) {
      return;
    }

    this.saving = true;
    const payload = this.buildPayload();
    const request = this.isEditMode && this.bookForm.id
      ? this.api.updateMarketplaceBook(this.bookForm.id, payload)
      : this.api.createMarketplaceBook(payload);

    request.subscribe(() => {
      this.saving = false;
      this.utils.toastSuccess(this.isEditMode ? 'Book updated successfully' : 'Book created successfully');
      this.closeBookModal();
      this.getBooks();
    }, err => {
      this.saving = false;
      this.utils.resErr(err);
    });
  }

  deleteBook(book): void {
    if (!book?.id) {
      this.utils.toastError('Invalid book record');
      return;
    }

    const bookName = String(book?.name || 'this book');
    const confirmDelete = confirm(
      `Are you sure you want to delete "${bookName}"? This will permanently remove the record from database.`
    );
    if (!confirmDelete) return;

    this.api.deleteMarketplaceBook(book.id).subscribe(() => {
      this.utils.toastSuccess('Book deleted successfully');
      this.getBooks();
    }, err => this.utils.resErr(err));
  }

  applyFilters(): void {
    this.page = 1;
  }

  clearFilters(): void {
    this.filters = {
      keyword: '',
      status: 'all',
      category: 'all'
    };
    this.page = 1;
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.keyword || '').trim()
      || this.filters.status !== 'all'
      || this.filters.category !== 'all';
  }

  handleFilterInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.applyFilters();
    }
  }

  pageChange(event): void {
    this.page = event;
    window.scrollTo(0, 0);
  }

  handleCopyToClipboard(str: any = ''): void {
    this.utils.copyToClipboard(str);
    this.utils.toastSuccess('ID copied to your clipboard successfully');
  }
}
